import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Compass, Scale, ClipboardList, Handshake, Globe } from 'lucide-react';
export function TrustMiniSection() {
  const {
    lang
  } = useLanguage();
  const differentiators = [{
    icon: <Compass className="w-6 h-6 text-inkova-lavender shrink-0" strokeWidth={1.5} data-id="element-439" />,
    title: 'Partimos de quién eres',
    titleEn: 'We start from who you are',
    text: 'No creamos una presencia genérica. Construimos desde tu identidad, tus habilidades y tu propósito — para que lo que proyectes sea auténtico y atraiga a los clientes correctos.',
    textEn: "We don't create a generic presence. We build from your identity, your skills, and your purpose — so that what you project is authentic and attracts the right clients."
  }, {
    icon: <Scale className="w-6 h-6 text-inkova-lavender shrink-0" strokeWidth={1.5} data-id="element-440" />,
    title: 'Legal y digital en un solo lugar',
    titleEn: 'Legal and digital in one place',
    text: 'Soy Notaria Certificada en Florida y experta en presencia digital. Puedes formalizar tu empresa y lanzar tu negocio sin buscar a nadie más.',
    textEn: 'I am a Certified Notary in Florida and an expert in digital presence. You can formalize your company and launch your business without looking for anyone else.'
  }, {
    icon: <ClipboardList className="w-6 h-6 text-inkova-lavender shrink-0" strokeWidth={1.5} data-id="element-441" />,
    title: 'Sabes exactamente qué sigue',
    titleEn: "You know exactly what's next",
    text: 'Sin improvisación. Cada paso tiene claridad — qué se entrega, cuánto tarda y qué necesitas de tu parte.',
    textEn: 'No improvisation. Every step has clarity — what is delivered, how long it takes, and what you need on your end.'
  }, {
    icon: <Handshake className="w-6 h-6 text-inkova-lavender shrink-0" strokeWidth={1.5} data-id="element-442" />,
    title: 'Acompañamiento real',
    titleEn: 'Real accompaniment',
    text: 'No recibes un template y un PDF. Hay una persona detrás de cada proyecto que entiende tu contexto, tu mercado y tu momento.',
    textEn: "You don't receive a template and a PDF. There is a person behind every project who understands your context, your market, and your moment."
  }, {
    icon: <Globe className="w-6 h-6 text-inkova-lavender shrink-0" strokeWidth={1.5} data-id="element-443" />,
    title: 'Construido para el emprendedor con propósito',
    titleEn: 'Built for the purpose-driven entrepreneur',
    text: 'Entiendo lo que significa construir algo desde cero — y acompaño ese proceso con claridad y propósito.',
    textEn: 'I understand what it means to build something from scratch — and I accompany that process with clarity and purpose.'
  }];
  return <section id="diferencia" className="py-32 lg:py-40 bg-white" data-id="element-444">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-445">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24 items-center" data-id="element-446">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} data-id="element-447">
            <div className="flex flex-col items-start gap-4 mb-8" data-id="element-448">
              <span className="block w-[40px] h-[1px] bg-inkova-primary/30" data-id="element-449" />
              <span className="inline-block font-sans text-[11px] font-semibold tracking-[3px] uppercase text-inkova-primary" data-id="element-450">
                {lang === 'es' ? 'NUESTRA DIFERENCIA' : 'OUR DIFFERENCE'}
              </span>
            </div>

            <h2 className="text-[32px] sm:text-[4vw] lg:text-[48px] font-serif text-inkova-dark leading-[1.1] tracking-[-0.5px] mb-8" data-id="element-451">
              {lang === 'es' ? <>
                  No es una agencia. Es un proceso construido desde tu{' '}
                  <em className="italic text-inkova-lavender" data-id="element-452">esencia.</em>
                </> : <>
                  It's not an agency. It's a process built from your{' '}
                  <em className="italic text-inkova-lavender" data-id="element-453">essence.</em>
                </>}
            </h2>
            <p className="text-[16px] lg:text-[18px] text-inkova-dark/70 font-light leading-[1.8] mb-16 max-w-[500px]" data-id="element-454">
              {lang === 'es' ? 'Hay mil opciones en internet. Inkova existe para quienes quieren algo diferente.' : 'There are a thousand options on the internet. Inkova exists for those who want something different.'}
            </p>

            <div className="flex flex-col gap-10" data-id="element-455">
              {differentiators.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              ease: 'easeOut',
              delay: index * 0.15
            }} className="flex gap-6 items-start relative" data-id="element-456">
                  {/* Fine divider line replacing the thick border-l */}
                  <div className="absolute left-[24px] top-[60px] bottom-[-40px] w-[1px] bg-inkova-border last:hidden" data-id="element-457" />

                  <div className="w-[48px] h-[48px] bg-inkova-cream rounded-full flex items-center justify-center shrink-0 border border-inkova-border z-10" data-id="element-458">
                    {item.icon}
                  </div>
                  <div className="pt-2" data-id="element-459">
                    <h4 className="text-[18px] font-serif font-medium text-inkova-dark mb-2" data-id="element-460">
                      {lang === 'es' ? item.title : item.titleEn}
                    </h4>
                    <p className="text-[15px] text-inkova-dark/70 font-light leading-[1.7] max-w-[460px]" data-id="element-461">
                      {lang === 'es' ? item.text : item.textEn}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} className="hidden lg:block relative overflow-hidden rounded-[32px]" data-id="element-462">
            <img src="https://cdn.magicpatterns.com/uploads/mM5W3ax4M2uK6T9rzzVPap/annysol-inkova.jpg.png" alt="Annysol Navarro reflexionando con una idea — Por qué Inkova" loading="lazy" className="w-full h-[700px] object-cover [object-position:center_top] animate-slow-zoom" data-id="element-463" />
            {/* Subtle dark tint overlay for cinematic feel */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[rgba(24,20,38,0.1)]" data-id="element-464" />
          </motion.div>
        </div>
      </div>
    </section>;
}